/**
 * Dispatch filters state and derivations: project/scope/worker/manager/start-time/
 * details multi-select filters, TopBar text search, and empty-project visibility,
 * with the same cross-curation pattern as the Schedule filters (options narrow
 * each other, selections are never hidden or silently dropped).
 */
import { useCallback, useMemo, useState } from "react";

import { toLocalDateKey } from "../../../utils/datetime";
import {
  getEffectiveFilterIds,
  mergeSelectedFilterOptions,
} from "../shared/filters/planningFilterModel";
import {
  DISPATCH_DETAIL_KIND_OPTIONS,
  collectDispatchDetailKindOptions,
  collectDispatchManagerOptions,
  collectDispatchProjectOptions,
  collectDispatchScopeOptions,
  collectDispatchStartTimeOptions,
  collectDispatchWorkerOptions,
  dispatchJobMatchesManagerFilter,
  dispatchJobMatchesSearch,
  dispatchJobMatchesStartTimeFilter,
  dispatchRowMatchesFilters,
  filterDispatchScopeRows,
  filterDispatchScopeRowsForSearch,
} from "./dispatchFilterModel";

/**
 * Filter state + derived option lists and filtered jobs/scopes for Dispatch.
 *
 * Curated option lists are narrowed by the *other* filters' raw selections;
 * already-selected options are merged back in so chips stay visible. The
 * effective ids applied to the page are validated against the base
 * (uncurated) lists, so selections only stop applying when the item leaves
 * the visible week entirely.
 *
 * @param {Object} params
 * @param {Array<Object>|null} params.jobs Weekly dispatch jobs.
 * @param {Array<Object>|null} params.scopes Weekly WorkScopeAspect rows.
 * @param {Array<Date>} params.datesOfWeek Visible days (already respects Show Weekends).
 * @param {Object|null} [params.singleProject=null] Optional project used to lock project scoping.
 * @returns {Object} Filter state, setters, options, and `filteredJobs`/`filteredScopes`.
 */
export default function useDispatchFilters({
  jobs,
  scopes,
  datesOfWeek,
  singleProject = null,
}) {
  const singleProjectId =
    singleProject?.id == null ? null : String(singleProject.id);
  // Show projects with no scopes in the visible week by default.
  const [hideEmptyProjects, setHideEmptyProjects] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProjectId, setFilterProjectId] = useState(() =>
    singleProjectId ? [singleProjectId] : []
  );
  const [filterScopeId, setFilterScopeId] = useState([]);
  const [filterWorkerId, setFilterWorkerId] = useState([]);
  const [filterManagerId, setFilterManagerId] = useState([]);
  const [filterStartTime, setFilterStartTime] = useState([]);
  const [filterDetailKind, setFilterDetailKind] = useState([]);
  const [filterHasNotes, setFilterHasNotes] = useState(false);
  const [filterNotesContain, setFilterNotesContain] = useState("");

  // Keep single-project embeds locked to the provided project id.
  const effectiveFilterProjectSelection = useMemo(
    () => (singleProjectId ? [singleProjectId] : filterProjectId),
    [singleProjectId, filterProjectId]
  );

  /**
   * Rows on currently visible days (weekend rows drop out when weekends are hidden).
   * @param {void}
   * @returns {Array<Object>}
   */
  const visibleScopeRows = useMemo(() => {
    const visibleDayKeys = new Set((datesOfWeek ?? []).map(toLocalDateKey));
    return (scopes ?? []).filter((row) => visibleDayKeys.has(String(row.date)));
  }, [scopes, datesOfWeek]);

  // Raw selection bag used for cross-curation (not yet validated against base lists).
  const rawFilterSelection = useMemo(
    () => ({
      projectIds: effectiveFilterProjectSelection,
      scopeIds: filterScopeId,
      workerIds: filterWorkerId,
      managerIds: filterManagerId,
      startTimes: filterStartTime,
      detailKinds: filterDetailKind,
      hasNotes: filterHasNotes,
      notesContain: filterNotesContain,
    }),
    [
      effectiveFilterProjectSelection,
      filterScopeId,
      filterWorkerId,
      filterManagerId,
      filterStartTime,
      filterDetailKind,
      filterHasNotes,
      filterNotesContain,
    ]
  );

  // Base (uncurated) option lists: validate selections and keep selected chips visible.
  const dispatchFilterProjectOptionsBase = useMemo(
    () => collectDispatchProjectOptions(jobs),
    [jobs]
  );
  const dispatchFilterScopeOptionsBase = useMemo(
    () => collectDispatchScopeOptions(visibleScopeRows),
    [visibleScopeRows]
  );
  const dispatchFilterWorkerOptionsBase = useMemo(
    () => collectDispatchWorkerOptions(visibleScopeRows),
    [visibleScopeRows]
  );
  const dispatchFilterManagerOptionsBase = useMemo(
    () => collectDispatchManagerOptions(jobs),
    [jobs]
  );
  const dispatchFilterStartTimeOptionsBase = useMemo(
    () => collectDispatchStartTimeOptions(jobs),
    [jobs]
  );
  // Fixed Has-* list; selections are validated against this full set.
  const dispatchFilterDetailKindOptionsBase = DISPATCH_DETAIL_KIND_OPTIONS;

  /**
   * Rows matching all raw filters except the omitted dimension (for option cascades).
   * @param {string} omit Filter dimension to ignore while cascading option lists.
   * @returns {Array<Object>}
   */
  const rowsMatchingFiltersExcept = useCallback(
    (omit) =>
      visibleScopeRows.filter((row) =>
        dispatchRowMatchesFilters(row, rawFilterSelection, { omit })
      ),
    [visibleScopeRows, rawFilterSelection]
  );

  // Available scope options reflect the other filters; selected scopes stay visible.
  const dispatchFilterScopeOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchScopeOptions(rowsMatchingFiltersExcept("scope")),
        dispatchFilterScopeOptionsBase,
        filterScopeId
      ),
    [rowsMatchingFiltersExcept, dispatchFilterScopeOptionsBase, filterScopeId]
  );

  // Available worker options reflect the other filters; selected workers stay visible.
  const dispatchFilterWorkerOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchWorkerOptions(rowsMatchingFiltersExcept("worker")),
        dispatchFilterWorkerOptionsBase,
        filterWorkerId
      ),
    [rowsMatchingFiltersExcept, dispatchFilterWorkerOptionsBase, filterWorkerId]
  );

  // Details Has-* options: only kinds present under the other filters.
  const dispatchFilterDetailKindOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchDetailKindOptions(rowsMatchingFiltersExcept("details")),
        dispatchFilterDetailKindOptionsBase,
        filterDetailKind
      ),
    [
      rowsMatchingFiltersExcept,
      dispatchFilterDetailKindOptionsBase,
      filterDetailKind,
    ]
  );

  /**
   * Jobs used to build manager/start-time/project option lists after other filters.
   * @param {string} omit Filter dimension to ignore while cascading option lists.
   * @returns {Array<Object>}
   */
  const jobsForJobLevelOptions = useCallback(
    (omit) => {
      const rowLevelActive =
        (omit !== "scope" && filterScopeId.length > 0) ||
        (omit !== "worker" && filterWorkerId.length > 0) ||
        (omit !== "details" && filterDetailKind.length > 0) ||
        (omit !== "notes" && filterHasNotes);
      const matchingJobIds = new Set(
        rowsMatchingFiltersExcept(omit).map((row) => String(row.job?.id))
      );
      return (jobs ?? []).filter((job) => {
        if (
          omit !== "project" &&
          effectiveFilterProjectSelection.length > 0 &&
          !effectiveFilterProjectSelection.includes(String(job.id))
        ) {
          return false;
        }
        if (
          omit !== "manager" &&
          !dispatchJobMatchesManagerFilter(job, filterManagerId)
        ) {
          return false;
        }
        if (
          omit !== "startTime" &&
          !dispatchJobMatchesStartTimeFilter(job, filterStartTime)
        ) {
          return false;
        }
        if (rowLevelActive || hideEmptyProjects) {
          return matchingJobIds.has(String(job.id));
        }
        return true;
      });
    },
    [
      jobs,
      rowsMatchingFiltersExcept,
      effectiveFilterProjectSelection,
      filterScopeId,
      filterWorkerId,
      filterDetailKind,
      filterHasNotes,
      filterManagerId,
      filterStartTime,
      hideEmptyProjects,
    ]
  );

  const dispatchFilterManagerOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchManagerOptions(jobsForJobLevelOptions("manager")),
        dispatchFilterManagerOptionsBase,
        filterManagerId
      ),
    [jobsForJobLevelOptions, dispatchFilterManagerOptionsBase, filterManagerId]
  );

  const dispatchFilterStartTimeOptions = useMemo(
    () =>
      mergeSelectedFilterOptions(
        collectDispatchStartTimeOptions(jobsForJobLevelOptions("startTime")),
        dispatchFilterStartTimeOptionsBase,
        filterStartTime
      ),
    [
      jobsForJobLevelOptions,
      dispatchFilterStartTimeOptionsBase,
      filterStartTime,
    ]
  );

  // Available project options reflect the other filters and empty-project rule.
  const dispatchFilterProjectOptions = useMemo(() => {
    const offeredJobs = jobsForJobLevelOptions("project");
    return mergeSelectedFilterOptions(
      collectDispatchProjectOptions(offeredJobs),
      dispatchFilterProjectOptionsBase,
      filterProjectId
    );
  }, [
    jobsForJobLevelOptions,
    dispatchFilterProjectOptionsBase,
    filterProjectId,
  ]);

  // Effective ids are validated against the base lists (everything in the
  // visible week), not the curated lists, so cross-curation never silently
  // drops an active filter. Single-project embeds stay locked to that project.
  const effectiveFilterProjectId = useMemo(
    () =>
      singleProjectId
        ? [singleProjectId]
        : getEffectiveFilterIds(filterProjectId, dispatchFilterProjectOptionsBase),
    [singleProjectId, filterProjectId, dispatchFilterProjectOptionsBase]
  );
  const effectiveFilterScopeId = useMemo(
    () => getEffectiveFilterIds(filterScopeId, dispatchFilterScopeOptionsBase),
    [filterScopeId, dispatchFilterScopeOptionsBase]
  );
  const effectiveFilterWorkerId = useMemo(
    () => getEffectiveFilterIds(filterWorkerId, dispatchFilterWorkerOptionsBase),
    [filterWorkerId, dispatchFilterWorkerOptionsBase]
  );
  const effectiveFilterManagerId = useMemo(
    () =>
      getEffectiveFilterIds(filterManagerId, dispatchFilterManagerOptionsBase),
    [filterManagerId, dispatchFilterManagerOptionsBase]
  );
  const effectiveFilterStartTime = useMemo(
    () =>
      getEffectiveFilterIds(filterStartTime, dispatchFilterStartTimeOptionsBase),
    [filterStartTime, dispatchFilterStartTimeOptionsBase]
  );
  const effectiveFilterDetailKind = useMemo(
    () =>
      getEffectiveFilterIds(
        filterDetailKind,
        dispatchFilterDetailKindOptionsBase
      ),
    [filterDetailKind, dispatchFilterDetailKindOptionsBase]
  );

  const effectiveFilterSelection = useMemo(
    () => ({
      projectIds: effectiveFilterProjectId,
      scopeIds: effectiveFilterScopeId,
      workerIds: effectiveFilterWorkerId,
      managerIds: effectiveFilterManagerId,
      startTimes: effectiveFilterStartTime,
      detailKinds: effectiveFilterDetailKind,
      hasNotes: filterHasNotes,
      notesContain: filterNotesContain,
    }),
    [
      effectiveFilterProjectId,
      effectiveFilterScopeId,
      effectiveFilterWorkerId,
      effectiveFilterManagerId,
      effectiveFilterStartTime,
      effectiveFilterDetailKind,
      filterHasNotes,
      filterNotesContain,
    ]
  );

  /**
   * Rows matching panel filters, then TopBar text search (Schedule order).
   * @param {void}
   * @returns {Array<Object>}
   */
  const filteredScopes = useMemo(() => {
    const toolbarFiltered = filterDispatchScopeRows(
      visibleScopeRows,
      effectiveFilterSelection
    );
    return filterDispatchScopeRowsForSearch(
      toolbarFiltered,
      searchQuery,
      jobs,
      effectiveFilterDetailKind
    );
  }, [
    visibleScopeRows,
    effectiveFilterSelection,
    searchQuery,
    jobs,
    effectiveFilterDetailKind,
  ]);

  /**
   * Jobs to render as rows: project/manager/start-time filters applied, and
   * jobs without remaining scope rows hidden when hide-empty is on, search is
   * active, or scope/worker/details filters are active. Jobs that match search
   * by number/name/manager stay visible even with no matching scopes.
   * @param {void}
   * @returns {Array<Object>}
   */
  const filteredJobs = useMemo(() => {
    const searchActive = Boolean(searchQuery?.trim());
    const rowLevelFilterActive =
      effectiveFilterScopeId.length > 0 ||
      effectiveFilterWorkerId.length > 0 ||
      effectiveFilterDetailKind.length > 0 ||
      filterHasNotes;
    const jobIdsWithRows = new Set(
      filteredScopes.map((row) => String(row.job?.id))
    );
    const scopedJobs = (jobs ?? []).filter((job) => {
      if (
        effectiveFilterProjectId.length > 0 &&
        !effectiveFilterProjectId.includes(String(job.id))
      ) {
        return false;
      }
      if (!dispatchJobMatchesManagerFilter(job, effectiveFilterManagerId)) {
        return false;
      }
      if (!dispatchJobMatchesStartTimeFilter(job, effectiveFilterStartTime)) {
        return false;
      }
      if (searchActive) {
        return (
          dispatchJobMatchesSearch(job, searchQuery) ||
          jobIdsWithRows.has(String(job.id))
        );
      }
      if (rowLevelFilterActive || hideEmptyProjects) {
        return jobIdsWithRows.has(String(job.id));
      }
      return true;
    });
    if (
      scopedJobs.length ||
      !singleProjectId ||
      !singleProject ||
      rowLevelFilterActive ||
      searchActive
    ) {
      return scopedJobs;
    }
    // Keep the locked project visible in compact single-project embeds.
    return [singleProject];
  }, [
    jobs,
    filteredScopes,
    effectiveFilterProjectId,
    effectiveFilterScopeId,
    effectiveFilterWorkerId,
    effectiveFilterDetailKind,
    filterHasNotes,
    effectiveFilterManagerId,
    effectiveFilterStartTime,
    hideEmptyProjects,
    searchQuery,
    singleProjectId,
    singleProject,
  ]);

  /**
   * Toggle Has Notes; clears Notes Contain when turned off.
   * @param {boolean} checked Whether the Has Notes filter is on.
   * @returns {void}
   */
  const setFilterHasNotesAndSync = useCallback((checked) => {
    setFilterHasNotes(checked);
    if (!checked) {
      setFilterNotesContain("");
    }
  }, []);

  /** Whether any panel-owned filter differs from its default (excludes Show Weekends). */
  const dispatchFilterActive =
    hideEmptyProjects ||
    (!singleProjectId && filterProjectId.length > 0) ||
    filterScopeId.length > 0 ||
    filterWorkerId.length > 0 ||
    filterManagerId.length > 0 ||
    filterStartTime.length > 0 ||
    filterDetailKind.length > 0 ||
    filterHasNotes;

  /**
   * Reset all panel-owned filters to defaults (Show Weekends is owned by WeeklyContext).
   * @param {void}
   * @returns {void}
   */
  const clearDispatchFilters = useCallback(() => {
    setHideEmptyProjects(false);
    if (!singleProjectId) {
      setFilterProjectId([]);
    }
    setFilterScopeId([]);
    setFilterWorkerId([]);
    setFilterManagerId([]);
    setFilterStartTime([]);
    setFilterDetailKind([]);
    setFilterHasNotes(false);
    setFilterNotesContain("");
  }, [singleProjectId]);

  return useMemo(
    () => ({
      hideEmptyProjects,
      setHideEmptyProjects,
      searchQuery,
      setSearchQuery,
      filterProjectId: effectiveFilterProjectSelection,
      setFilterProjectId,
      filterScopeId,
      setFilterScopeId,
      filterWorkerId,
      setFilterWorkerId,
      filterManagerId,
      setFilterManagerId,
      filterStartTime,
      setFilterStartTime,
      filterDetailKind,
      setFilterDetailKind,
      filterHasNotes,
      setFilterHasNotes: setFilterHasNotesAndSync,
      filterNotesContain,
      setFilterNotesContain,
      dispatchFilterProjectOptions,
      dispatchFilterScopeOptions,
      dispatchFilterWorkerOptions,
      dispatchFilterManagerOptions,
      dispatchFilterStartTimeOptions,
      dispatchFilterDetailKindOptions,
      filteredJobs,
      filteredScopes,
      dispatchFilterActive,
      clearDispatchFilters,
    }),
    [
      hideEmptyProjects,
      searchQuery,
      effectiveFilterProjectSelection,
      filterScopeId,
      filterWorkerId,
      filterManagerId,
      filterStartTime,
      filterDetailKind,
      filterHasNotes,
      setFilterHasNotesAndSync,
      filterNotesContain,
      dispatchFilterProjectOptions,
      dispatchFilterScopeOptions,
      dispatchFilterWorkerOptions,
      dispatchFilterManagerOptions,
      dispatchFilterStartTimeOptions,
      dispatchFilterDetailKindOptions,
      filteredJobs,
      filteredScopes,
      dispatchFilterActive,
      clearDispatchFilters,
    ]
  );
}
